import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../../settings";
import { COIN_COLLECT_CONFIG } from "../HoldAndWinCoinCollect/HoldAndWinCoinCollectController";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";

// Constants
const RENDER_PIPELINE_ALPHA = 0.001;
const VISIBLE_ALPHA = 1;

// Configuration for all orientations (using portrait variant only)
const DECORATION_CONFIG = {
  widthMultiplier: 1.108,
  heightMultiplier: 1.17,
  verticalOffsetMultiplier: 0, // Multiplied by symbolHeight
  horizontalOffsetMultiplier: 0 , // Multiplied by symbolWidth
};

/**
 * ReelFrameDecorationController
 * Manages decorative spine animations (claws) for the reel frame during Hold and Win
 * Uses dual spine system for portrait and landscape orientations
 */
class ReelFrameDecorationController {
  constructor(
    container,
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
    orientation
  ) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensions = dimensions;
    this.dividerDimensions = dividerDimensions;
    this.orientation = orientation;

    // Spine asset data
    this.decorationSpineData = {
      skeleton: "reel_frame-skeleton",
      atlas: "reel_frame-atlas",
    };

    // End spine asset data (glow effect)
    this.endSpineData = {
      skeleton: "hns_end-skeleton",
      atlas: "hns_end-atlas",
    };

    // Single spine instance (portrait variant for all orientations)
    this.decorationSpine = null;

    // End spine (glow effect at end of Hold and Win)
    this.endSpine = null;

    // Container for decoration spine
    this.decorationContainer = null;

    this.initialized = false;
    this.isCleanedUp = false;

    this.initializeDecorationSpines();
    this.initializeEndSpine();
  }

  /**
   * Check if controller is ready
   */
  isReady() {
    return (
      this.decorationSpine?.state &&
      this.initialized &&
      !this.isCleanedUp
    );
  }

  /**
   * Create a decoration spine instance
   */
  createDecorationSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.decorationSpineData.skeleton) ||
        !assets.has(this.decorationSpineData.atlas)
      ) {
        return null;
      }

      const skeletonData = PIXI.Assets.get(this.decorationSpineData.skeleton);
      const atlasData = PIXI.Assets.get(this.decorationSpineData.atlas);

      if (!skeletonData || !atlasData) {
        return null;
      }

      const decorationSpine = Spine.from({
        skeleton: this.decorationSpineData.skeleton,
        atlas: this.decorationSpineData.atlas,
      });

      if (
        !decorationSpine ||
        !decorationSpine.skeleton ||
        !decorationSpine.state
      ) {
        return null;
      }

      return decorationSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animation names from a spine instance
   */
  getAvailableAnimations(spine) {
    if (!spine?.skeleton?.data?.animations) {
      return [];
    }
    return spine.skeleton.data.animations.map((anim) => anim.name);
  }

  /**
   * Check if spine has a specific animation
   */
  hasAnimation(spine, animationName) {
    return this.getAvailableAnimations(spine).includes(animationName);
  }

  /**
   * Apply scaling to match reel frame dimensions
   */
  applySpineScaling(spine) {
    if (!spine || !this.symbolHeight || !this.dimensions) {
      return;
    }

    const spineBounds = spine.getLocalBounds();
    if (!spineBounds || spineBounds.width <= 0 || spineBounds.height <= 0) {
      requestAnimationFrame(() => {
        this.applySpineScaling(spine);
      });
      return;
    }

    // Calculate target dimensions matching ReelFront feature frame
    const dividerCount = Settings.REEL_COUNT - 1;
    const dividerWidth = this.dividerDimensions?.width ?? 0;

    const targetWidth =
      (this.symbolWidth * Settings.REEL_COUNT + dividerWidth * dividerCount) *
      DECORATION_CONFIG.widthMultiplier;
    const targetHeight =
      this.symbolHeight * Settings.SYMBOLS_VISIBLE * DECORATION_CONFIG.heightMultiplier;

    const scaleX = targetWidth / spineBounds.width;
    const scaleY = targetHeight / spineBounds.height;

    spine.scale.set(scaleX, scaleY);

    // Apply offsets based on symbol size
    const offsetX = this.symbolWidth * DECORATION_CONFIG.horizontalOffsetMultiplier;
    const offsetY = this.symbolHeight * DECORATION_CONFIG.verticalOffsetMultiplier;

    spine.position.set(offsetX, offsetY);
  }

  /**
   * Initialize decoration spine instance (portrait variant only)
   */
  initializeDecorationSpines() {
    // Create decoration container and spine
    this.decorationContainer = new PIXI.Container();
    this.decorationContainer.name = "decoration_container";
    this.decorationContainer.sortableChildren = true;
    this.container.addChild(this.decorationContainer);

    this.decorationSpine = this.createDecorationSpine();
    if (this.decorationSpine) {
      this.decorationSpine.zIndex = 10;
      this.decorationContainer.addChild(this.decorationSpine);

      // Apply scaling
      this.applySpineScaling(this.decorationSpine);

      // Play portrait animation (used for all orientations)
      if (this.hasAnimation(this.decorationSpine, "frame_claws_portrait")) {
        this.decorationSpine.state.setAnimation(0, "frame_claws_portrait", true);
      }

      // Make visible
      this.decorationSpine.alpha = VISIBLE_ALPHA;
    }

    this.initialized = true;
  }

  /**
   * Create end spine instance (glow effect)
   */
  createEndSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.endSpineData.skeleton) ||
        !assets.has(this.endSpineData.atlas)
      ) {
        return null;
      }

      const endSpine = Spine.from({
        skeleton: this.endSpineData.skeleton,
        atlas: this.endSpineData.atlas,
      });

      if (!endSpine || !endSpine.skeleton || !endSpine.state) {
        return null;
      }

      return endSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize end spine (glow effect)
   */
  initializeEndSpine() {
    try {
      this.endSpine = this.createEndSpine();
      if (!this.endSpine) {
        return;
      }

      this.container.addChild(this.endSpine);
      this.endSpine.zIndex = 2;
      this.endSpine.alpha = RENDER_PIPELINE_ALPHA; // Hidden initially

      // Apply same scale and position as coin collect spines
      this.applyEndSpineScaleAndPosition();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Apply scale and position to end spine - matches coin collect spine container
   */
  applyEndSpineScaleAndPosition() {
    if (!this.endSpine || !this.symbolHeight) {
      return;
    }

    // Use the SAME calculation as CoinCollect controller
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let baseScale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply scale multiplier from config (same as coin collect)
    baseScale *= COIN_COLLECT_CONFIG.scaleMultiplier;

    // Apply scale
    this.endSpine.scale.set(baseScale);

    // Calculate same offsets as coin collect
    const horizontalOffset =
      this.symbolWidth * COIN_COLLECT_CONFIG.horizontalOffsetMultiplier;
    const verticalOffset =
      this.symbolHeight * COIN_COLLECT_CONFIG.verticalOffsetMultiplier;

    // Apply position
    this.endSpine.position.set(horizontalOffset, verticalOffset);
  }

  /**
   * Play end animation (glow effect)
   */
  playEndAnimation() {
    return new Promise((resolve) => {
      if (!this.endSpine || !this.endSpine.state) {
        resolve();
        return;
      }

      try {
        // Make spine visible
        this.endSpine.alpha = VISIBLE_ALPHA;

        // Clear any previous animations and listeners
        this.endSpine.state.clearTracks();
        this.endSpine.state.clearListeners();

        // Play feature end sound
        soundManager.play(SOUND_NAME.feature_end);

        // Play the end animation (non-looping)
        this.endSpine.state.setAnimation(0, "end", false);

        // Add listener for completion
        this.endSpine.state.addListener({
          complete: (entry) => {
            if (entry.animation.name === "end") {
              // Hide spine after animation completes
              if (this.endSpine) {
                this.endSpine.alpha = RENDER_PIPELINE_ALPHA;
              }
              resolve();
            }
          },
        });
      } catch (error) {
        resolve();
      }
    });
  }

  /**
   * Update orientation (no-op since we use portrait variant for all orientations)
   */
  updateOrientation(newOrientation) {
    this.orientation = newOrientation;
    // Portrait variant is always visible, no orientation switching needed
  }

  /**
   * Update dimensions for responsive scaling
   */
  resizeDecoration(
    newSymbolWidth,
    newSymbolHeight,
    newDimensions,
    newDividerDimensions,
    newOrientation
  ) {
    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;
    this.dimensions = newDimensions;
    this.dividerDimensions = newDividerDimensions;
    this.orientation = newOrientation;

    if (this.decorationSpine) {
      this.applySpineScaling(this.decorationSpine);
    }
    if (this.endSpine) {
      this.applyEndSpineScaleAndPosition();
    }

    this.updateOrientation(newOrientation);
  }

  /**
   * Clean up spine resources and references
   */
  cleanup() {
    this.isCleanedUp = true;

    // Cleanup decoration spine
    if (this.decorationSpine && !this.decorationSpine.destroyed) {
      try {
        if (this.decorationSpine.state) {
          this.decorationSpine.state.clearTracks();
          this.decorationSpine.state.clearListeners();
        }
        if (this.decorationSpine.parent) {
          this.decorationSpine.parent.removeChild(this.decorationSpine);
        }
        this.decorationSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    // Cleanup end spine
    if (this.endSpine && !this.endSpine.destroyed) {
      try {
        if (this.endSpine.state) {
          this.endSpine.state.clearTracks();
          this.endSpine.state.clearListeners();
        }
        if (this.endSpine.parent) {
          this.endSpine.parent.removeChild(this.endSpine);
        }
        this.endSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    // Cleanup container
    if (this.decorationContainer && !this.decorationContainer.destroyed) {
      try {
        if (this.decorationContainer.parent) {
          this.decorationContainer.parent.removeChild(this.decorationContainer);
        }
        this.decorationContainer.destroy({ children: true });
      } catch (error) {
        // Silent fail
      }
    }

    // Clear references
    this.decorationSpine = null;
    this.endSpine = null;
    this.decorationContainer = null;
    this.container = null;
    this.decorationSpineData = null;
    this.endSpineData = null;
    this.initialized = false;
  }
}

export default ReelFrameDecorationController;
